/**
 * rc108/rc110 — Ajustes › Integraciones: ByteOLT (planta GPON) + ByteACS (TR-069).
 *
 * Cada card: URL + API key (cifrada server-side, aquí solo enmascarada) +
 * toggle + scopes requeridos + "Probar conexión" contra el /ext/ping del otro
 * sistema (vía proxy del API — el navegador nunca ve las llaves).
 *
 * ByteOLT activo → alta de fibra (picker de ONUs) y desaprovisionar al cancelar.
 * ByteACS activo → al alta se registra el módem (WAN PPPoE + WiFi en su primer
 * Inform) y al cancelar se manda al ALMACÉN.
 */
import { useEffect, useState, type ReactNode } from "react";
import { apexDe } from "@/lib/branding";
import { toast } from "sonner";
import { Cable, CheckCircle2, Loader2, Radio, Router as RouterIcon, XCircle } from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import {
  useByteoltSettings, useGuardarByteolt, useProbarByteolt,
  useByteacsSettings, useGuardarByteacs, useProbarByteacs,
  type ByteoltSettings, type ByteoltProbeResult,
} from "@/features/integraciones/api";

type GuardarArgs = { enabled: boolean; base_url?: string | null; api_key?: string | null };

function IntegracionCard({
  icon, nombre, tagline, descripcion, keyPrefix, urlPlaceholder,
  settings, isLoading, guardar, probar, habilita,
}: {
  icon: ReactNode;
  nombre: string;
  tagline: string;
  descripcion: string;
  keyPrefix: string;
  urlPlaceholder: string;
  settings: ByteoltSettings | undefined;
  isLoading: boolean;
  guardar: UseMutationResult<ByteoltSettings, unknown, GuardarArgs>;
  probar: UseMutationResult<ByteoltProbeResult, unknown, void>;
  habilita: ReactNode;
}) {
  const [enabled, setEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [probeResult, setProbeResult] = useState<ByteoltProbeResult | null>(null);

  useEffect(() => {
    if (settings) {
      setEnabled(settings.enabled);
      setBaseUrl(settings.base_url ?? "");
      setApiKey("");
    }
  }, [settings]);

  async function handleGuardar() {
    try {
      await guardar.mutateAsync({
        enabled,
        base_url: baseUrl.trim() || null,
        // Solo se manda la llave si se tecleó una nueva (el server además
        // ignora valores enmascarados — patrón rc86 anti-corrupción).
        api_key: apiKey.trim() || null,
      });
      setApiKey("");
      toast.success(`Integración ${nombre} guardada`);
    } catch (e) {
      toast.error("No se pudo guardar", { description: (e as ApiError).message ?? "Error" });
    }
  }

  async function handleProbar() {
    setProbeResult(null);
    try {
      setProbeResult(await probar.mutateAsync());
    } catch (e) {
      setProbeResult({ success: false, error: (e as ApiError).message ?? "Error" });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-muted/20 p-6 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin" /> Cargando {nombre}…
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-muted/20 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-primary/15 text-primary">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {nombre}{" "}
            <span className="font-mono text-[10px] font-normal text-muted-foreground">
              · {tagline}
            </span>
          </div>
          <p className="text-[10.5px] text-muted-foreground">{descripcion}</p>
        </div>
        <span
          className={cn(
            "rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider",
            enabled ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground",
          )}
        >
          {enabled ? "activa" : "desactivada"}
        </span>
        <button
          type="button"
          onClick={() => setEnabled((v) => !v)}
          aria-label={`Habilitar integración ${nombre}`}
          className={cn(
            "relative h-5 w-9 flex-none rounded-full border transition-colors",
            enabled ? "border-primary bg-primary" : "border-border bg-muted",
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 h-3.5 w-3.5 rounded-full bg-background transition-all",
              enabled ? "left-[18px] bg-white" : "left-0.5",
            )}
          />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label>URL base</Label>
          <Input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder={urlPlaceholder}
            className="font-mono"
          />
        </div>
        <div>
          <Label>API key ({keyPrefix})</Label>
          <Input
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            type="text"
            className="font-mono"
            placeholder={
              settings?.api_key_set
                ? `Guardada · ${settings.api_key_masked ?? "••••"} — escribe una nueva para cambiarla`
                : "Pega aquí la llave completa"
            }
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Se emite en {nombre} › Ajustes › API Keys. Cópiala con el botón
            (un 401 al probar casi siempre es llave mal pegada).
          </p>
        </div>
      </div>

      <div className="mt-2">
        <Label>Scopes requeridos en la llave</Label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {(settings?.required_scopes ?? []).map((s) => (
            <span
              key={s}
              className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 font-mono text-[10px] text-primary"
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={handleGuardar} disabled={guardar.isPending}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Button>
        <Button
          size="sm" variant="outline" onClick={handleProbar}
          disabled={probar.isPending || !settings?.api_key_set}
          title={!settings?.api_key_set ? "Primero guarda la URL y la llave" : undefined}
        >
          <Cable size={12} className="mr-1" />
          {probar.isPending ? "Probando…" : "Probar conexión"}
        </Button>
        {probeResult && (
          <span
            className={cn(
              "flex items-center gap-1.5 font-mono text-[10.5px]",
              probeResult.success
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-destructive",
            )}
          >
            {probeResult.success ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
            {probeResult.success
              ? [
                  `OK · llave ${probeResult.key_name ?? "?"}`,
                  probeResult.olts != null ? `${probeResult.olts} OLTs` : null,
                  probeResult.onus_huerfanas != null
                    ? `${probeResult.onus_huerfanas} ONUs por autorizar`
                    : null,
                ].filter(Boolean).join(" · ")
              : probeResult.error}
          </span>
        )}
      </div>
      {probeResult?.success && probeResult.warning && (
        <p className="mt-2 text-[10.5px] text-amber-600 dark:text-amber-400">
          ⚠ {probeResult.warning}
        </p>
      )}

      <div className="mt-4 rounded-md border border-primary/25 bg-primary/5 p-3 text-[11px] leading-relaxed text-primary">
        <strong>Qué habilita:</strong> {habilita}
      </div>
    </div>
  );
}

export function IntegracionesSection() {
  const olt = useByteoltSettings();
  const acs = useByteacsSettings();

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        Conexiones con otros sistemas del ecosistema. Las llaves se guardan
        cifradas y nunca bajan al navegador — ByteISP habla con el otro sistema
        desde el servidor.
      </p>

      <IntegracionCard
        icon={<Radio size={15} />}
        nombre="ByteOLT"
        tagline="planta GPON"
        descripcion="Alta, ligue y desaprovisionamiento de ONUs de fibra directo desde ByteISP."
        keyPrefix="bolt_"
        urlPlaceholder={`https://olt.${apexDe()}`}
        settings={olt.data}
        isLoading={olt.isLoading}
        guardar={useGuardarByteolt()}
        probar={useProbarByteolt()}
        habilita={
          <>
            con la integración activa, los planes de <strong>fibra</strong> se
            dan de alta eligiendo la ONU (por autorizar o ya aprovisionada sin
            cliente) directamente de ByteOLT, y al <strong>cancelar</strong> un
            contrato de fibra la ONU se desaprovisiona sola. Sin integración,
            el servicio de fibra no está disponible.
          </>
        }
      />

      <IntegracionCard
        icon={<RouterIcon size={15} />}
        nombre="ByteACS"
        tagline="gestión TR-069 de módems"
        descripcion="Registro del módem al alta (WAN PPPoE + WiFi en su primer Inform) y baja a almacén al cancelar."
        keyPrefix="bacs_"
        urlPlaceholder={`https://acs.${apexDe()}`}
        settings={acs.data}
        isLoading={acs.isLoading}
        guardar={useGuardarByteacs()}
        probar={useProbarByteacs()}
        habilita={
          <>
            con la integración activa, el <strong>alta de fibra</strong> registra
            el módem en ByteACS (aprovisiona solo en su primer Inform: WAN PPPoE
            con las credenciales del contrato + VLAN + WiFi) y la{" "}
            <strong>cancelación</strong> lo manda al almacén. Requiere la
            integración ByteOLT activa — es parte del mismo flujo de fibra.
          </>
        }
      />
    </div>
  );
}
